import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { DeleteJson, GetJson, PostJson, PutJson } from '@awp/helpers/fetchReact';
import { FORM_MODE, Permissions, REST_API } from '@awp/constants';
import { Card, Col, Container, Row } from 'react-bootstrap';
import {
  FormColorInput,
  FormInput,
  FormRadio,
  FormRadioGroup,
  FormSelect,
  FormTextArea,
  FormListWithAction,
} from '@awp/components/Common/AwpFormInputs';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import { format } from '@awp/helpers/numFormat';
import { toast } from 'react-toastify';
import AwpValidationErrorModal from '@awp/components/Common/AwpValidationErrorModal';
import AwpYesNoDialog from '@awp/components/Common/AwpYesNoDialog';
import AwpFormActionButtons from '@awp/components/Common/AwpFormActionButtons';
import AwpSpinner from '@awp/components/Common/AwpSpinner';
import { isIntStr, isJsonValueEmpty, isValueEmpty } from '@awp/helpers/utilsReact';

import * as ColorUtils from '@awp/helpers/ColorUtils';
import {
  selectAllDataTypes,
  selectAllGraphVisualizationTypes,
  selectAllStandardgraphaxises,
  selectAllTransformTypes,
  selectAllUnitofmeasures,
} from '@stores/observationTypeStatusesSlice';
import CodeTableAdmin from '@awp/components/ObservationTypeAdmin/CodeTable/CodeTableAdmin';
import PropTypes from 'prop-types';
import isEqual from 'lodash/isEqual';
import ObservationTypeService, {
  STANDARD_GRAPH_AXIS_ID_DEFAULT,
} from '@components/ObservationTypeAdmin/ObservationTypeService';

const ObservationTypeEdit = ({
  setMode,
  mode,
  observationTypeId,
  data,
  setData,
  setLoading,
  loading,
  categoryDropdownOptions,
}) => {
  const allAllGraphVisualizationTypes = useSelector(selectAllGraphVisualizationTypes);
  const allStandardGraphAxises = useSelector(selectAllStandardgraphaxises);
  const allUnitOfMeasures = useSelector(selectAllUnitofmeasures);
  const allDataTypes = useSelector(selectAllDataTypes);
  const allTransformTypes = useSelector(selectAllTransformTypes);

  const [isAvalancheObs, setIsAvalancheObs] = useState(false);
  const [observationTypeData, setObservationTypeData] = useState({});
  const [entityErrors, setEntityErrors] = useState(null);
  const [codeTableErrors, setCodeTableErrors] = useState({});
  const [standardGraphAxisOptions, setStandardgraphaxisOptions] = useState([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [formatDisplayExample, setFormatDisplayExample] = useState('e.g: 1000.0123456789 => 1,000');
  const [initialDto, setInitialDto] = useState({});
  const [saveCodeTable, setSaveCodeTable] = useState(false);

  const crudService = new ObservationTypeService(mode);
  const { title, readOnly, successMessage, buttonLabel, entityName } = crudService.crud;

  const [isOriginal, setIsOriginal] = useState(false);
  const [linkedObservations, setLinkedObservations] = useState([]);

  const AvalancheCategories = new Set(['Avalanche Observation', 'Avalanche Control', 'Avalanche Hazard Forecast']);

  useEffect(() => {
    if (mode === FORM_MODE.CREATE) {
      setObservationTypeData({ Name: '', Definition: '' });
      setInitialDto(crudService.mapInitialValues({ entity: observationTypeData }));
      return;
    }

    setLoading(true);
    const url = REST_API.OBSERVATION_TYPES + observationTypeId;

    GetJson(url)
      .then((response) => response.json())
      .then((ot) => {
        setObservationTypeData(ot);
        formatSampleValue(ot.DisplayFormat);
        setIsAvalancheObs(ot.ObservationCategory == 0);
        if (isJsonValueEmpty(standardGraphAxisOptions)) {
          let selectedUnit = {
            value: ot.UnitOfMeasureId,
            label: ot.Abbreviation,
          };
          let newOptions = filterStandardgraphaxisOptions(selectedUnit);
          setStandardgraphaxisOptions(convertDropdownOptions(newOptions));
        }
        setInitialDto(crudService.mapInitialValues({ entity: ot }));
        setLoading(false);
      });

    if (mode !== FORM_MODE.CREATE) {
      const url = REST_API.OBSERVATION_TYPES + `linked/` + observationTypeId;

      GetJson(url)
        .then((response) => response.json())
        .then((data) => {
          if (data != null) {
            setLinkedObservations(data?.Observations ?? []);
            setIsOriginal(data?.IsOriginal);
          }
        })
        .catch((err) => {
          setLinkedObservations([]);
        });
    }
  }, []);

  const filterStandardgraphaxisOptions = (unitOpt) => {
    if (isValueEmpty(unitOpt) || isValueEmpty(unitOpt.value)) return [];
    if (isValueEmpty(allStandardGraphAxises) || allStandardGraphAxises.length === 0) return [];
    let newOptions = [];
    allStandardGraphAxises.map((s) => {
      if (s.UnitId == unitOpt.value) newOptions.push(s);
    });
    return newOptions;
  };

  const convertDropdownOptions = (options) => {
    if (isValueEmpty(options) || options.length === 0) return [];
    let newOptions = [];
    options.map((t) => {
      newOptions.push({ value: t.Id, label: t.Name });
    });
    return newOptions;
  };

  const updateObservationType = (setSubmitting, resetForm, setErrors, observationtypeDto) => {
    const url = REST_API.OBSERVATION_TYPES + observationtypeDto.Id;

    PutJson(url, observationtypeDto)
      .then(async (response) => {
        if (response.ok) {
          toast.success(successMessage);
          const ot = await response.json();
          setObservationTypeData(ot);
          updateData(ot);

          const updatedDTO = crudService.mapInitialValues({ entity: ot });
          setInitialDto(updatedDTO);
          resetForm({
            values: updatedDTO,
          });
          setMode(FORM_MODE.VIEW);
          setIsAvalancheObs(ot.ObservationCategory == 0);
          setSaveCodeTable(true);
        } else if (response.status === 412) {
          const errors = await response.json();
          setErrors(errors);
          if (errors.entity) setEntityErrors({ ObservationType: errors.entity });
        }
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  const createObservationType = (setSubmitting, resetForm, setErrors, observationtypeDto) => {
    const url = REST_API.OBSERVATION_TYPES;

    PostJson(url, observationtypeDto)
      .then(async (response) => {
        if (response.ok) {
          const observationtype = await response.json();
          setObservationTypeData(observationtype);

          const updatedDTO = crudService.mapInitialValues({ entity: observationtype });
          setInitialDto(updatedDTO);
          resetForm({
            values: updatedDTO,
          });

          updateData(observationtype);
          toast.success(successMessage);
          setMode(FORM_MODE.VIEW);
          setIsAvalancheObs(observationtype.ObservationCategory == 0);
          setSaveCodeTable(true);
        } else if (response.status === 412) {
          const errors = await response.json();
          setErrors(errors);
          if (errors.entity) setEntityErrors({ ObservationType: errors.entity });
        }
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  const deleteObservationType = () => {
    const id = observationTypeData.Id;
    const url = REST_API.OBSERVATION_TYPES + id;

    DeleteJson(url, {})
      .then(async (response) => {
        if (response.ok) {
          setObservationTypeData({});
          removeData(id);
          toast.success(`${entityName} has been deleted`);
          setMode(FORM_MODE.LIST);
        } else if (response.status === 412) {
          const errors = await response.json();
          //setErrors(errors);
          if (errors.entity) setEntityErrors({ ObservationType: errors.entity });
        }
      })
      .finally(() => {
        //setSubmitting(false);
      });
  };

  const handleSubmit = (values, { setSubmitting, resetForm, setErrors }) => {
    setSubmitting(true);

    const observationtypeDto = crudService.mapFormToDTO({
      entity: observationTypeData,
      values,
      getdataTypeId,
    });

    if (mode === FORM_MODE.EDIT) {
      updateObservationType(setSubmitting, resetForm, setErrors, observationtypeDto);
    } else {
      createObservationType(setSubmitting, resetForm, setErrors, observationtypeDto);
    }
  };

  const updateData = (ot) => {
    if (mode === FORM_MODE.EDIT) {
      const index = data.findIndex((x) => x.Id === ot.Id);
      if (index < 0) return;
      data[index] = ot;
      setData([...data]);
    } else {
      setData([...data, ot]);
    }
  };

  const removeData = (Id) => {
    if (mode === FORM_MODE.EDIT) {
      let _data = [...data];
      let _removedData = [];
      _data.map((o) => {
        if (o.Id !== Id) {
          _removedData.push(o);
        }
      });
      setData([..._removedData]);
    }
  };

  const onClickDelete = () => {
    setShowDeleteConfirm(true);
  };

  const getdataTypeId = (name) => {
    if (!allDataTypes || allDataTypes.length === 0) return null;
    let s = allDataTypes.find((d) => d.Name == name);
    return s.Id ?? null;
  };

  const getDataTypeName = () => {
    if (!isValueEmpty(observationTypeData) && !isValueEmpty(observationTypeData.WagDataType))
      return observationTypeData.WagDataType.Name ?? 'Numeric';
    return 'Numeric';
  };

  const onUnitPostChange = (val, props) => {
    props.setFieldValue('standardgraphaxisid', STANDARD_GRAPH_AXIS_ID_DEFAULT); // eslint-disable-line react/prop-types
    let newOptions = filterStandardgraphaxisOptions(val);
    setStandardgraphaxisOptions(convertDropdownOptions(newOptions));
  };

  const onChangeDataFormat = (e, props) => {
    let val = e.target.value;
    props.setFieldValue('displayformat', val);
    formatSampleValue(val); // eslint-disable-line react/prop-types
  };

  const formatSampleValue = (val) => {
    let sampleVal = '1000.0123456789';
    if (isValueEmpty(val)) val = '';
    let df = 'e.g: ' + sampleVal + ' => ' + format(sampleVal, val);
    setFormatDisplayExample(df);
  };

  const isDisableLimit = (props) => {
    return props.values.isactive !== 'Numeric'; // eslint-disable-line react/prop-types
  };

  const setColSpanOrder = (span, order) => {
    return { span: span, order: order };
  };

  const columns = [
    { label: 'Name', key: 'Name' },
    { label: 'Display Name', key: 'DisplayName' },
  ];

  const categoryOnChange = (opt) => {
    const isAvalanche = AvalancheCategories.has(opt.label);
    setIsAvalancheObs(isAvalanche);
  };

  return (
    <>
      {loading ? (
        <Container>
          <AwpSpinner />
        </Container>
      ) : (
        <>
          <Formik
            initialValues={crudService.mapInitialValues({ entity: observationTypeData })}
            validateOnMount={true}
            initialTouched={{
              name: true,
              displayname: true,
              definition: true,
              displayformat: true,
              lowerlimit: true,
              upperlimit: true,
              graphcolor: true,
              unitofmeasureid: true,
            }}
            validationSchema={Yup.object({
              name: Yup.string().max(50, 'Maximum 50 characters are allowed').required('Name is a required field'),
              displayname: Yup.string()
                .label('Display name')
                .max(50, '${label} must not exceed ${max} characters')
                .required('${label} is a required field'),
              unitofmeasureid: Yup.object().test('checkunitofmeasureid', function () {
                if (
                  isValueEmpty(this.parent.unitofmeasureid) ||
                  isValueEmpty(this.parent.unitofmeasureid.label) ||
                  this.parent.unitofmeasureid.value === 0
                ) {
                  return this.createError({
                    message: 'Display Unit is required',
                    path: 'unitofmeasureid',
                  });
                }
                return true;
              }),
              definition: Yup.string().max(1000, 'Maximum 1000 characters are allowed'),
              displayformat: Yup.string().max(3, 'Display Format cannot exceed 3 characters'),
              lowerlimit: Yup.string()
                .max(10, 'Maximum 10 characters are allowed')
                .test('checklowerlimit', function () {
                  if (!isValueEmpty(this.parent.lowerlimit) && !isIntStr(this.parent.lowerlimit)) {
                    return this.createError({
                      message: 'Please enter a valid number',
                      path: 'lowerlimit',
                    });
                  }
                  if (
                    !isValueEmpty(this.parent.lowerlimit) &&
                    !isValueEmpty(this.parent.upperlimit) &&
                    Number(this.parent.lowerlimit) > Number(this.parent.upperlimit)
                  ) {
                    return this.createError({
                      message: 'Lower limit should be less than upper limit',
                      path: 'lowerlimit',
                    });
                  }
                  return true;
                }),
              upperlimit: Yup.string()
                .max(10, 'Maximum 10 characters are allowed')
                .test('checkupperlimit', function () {
                  if (!isValueEmpty(this.parent.upperlimit) && !isIntStr(this.parent.upperlimit)) {
                    return this.createError({
                      message: 'Please enter a valid number',
                      path: 'upperlimit',
                    });
                  }
                  if (
                    !isValueEmpty(this.parent.lowerlimit) &&
                    !isValueEmpty(this.parent.upperlimit) &&
                    Number(this.parent.lowerlimit) > Number(this.parent.upperlimit)
                  ) {
                    return this.createError({
                      message: 'Upper limit should be greater than lower limit',
                      path: 'upperlimit',
                    });
                  }
                  return true;
                }),
              graphcolor: Yup.string()
                .nullable()
                .test('validateColorString', function () {
                  if (!isValueEmpty(this.parent.standardgraphaxisid)) {
                    const colorValue = this.parent.graphcolor;
                    if (!ColorUtils.isValidColorString(colorValue) && colorValue) {
                      return this.createError({
                        message: 'Please enter a valid color format',
                        path: 'graphcolor',
                      });
                    }
                  }
                  return true;
                }),
              observationcategoryid: Yup.object()
                .nullable()
                .test('observationcategoryid-required', 'Category is required', function (value) {
                  return mode == FORM_MODE.CREATE ? value && value.value : true;
                }),
            })}
            onSubmit={handleSubmit}
          >
            {(props) => (
              <Form>
                <span className="awp-text-btn" onClick={() => setMode('list')}>
                  List Observation Types
                </span>
                <span className="mx-3">&gt;</span>
                <span>{title}</span>
                <Card border="light" className="page-card">
                  <Card.Header>
                    <Row className="mt-4"></Row>
                    {crudService.crud.getHeader()}
                  </Card.Header>
                  <Card.Body>
                    <Row>
                      <Col
                        sm={setColSpanOrder(6, 1)}
                        md={setColSpanOrder(6, 1)}
                        lg={setColSpanOrder(4, 1)}
                        className="mb-4"
                      >
                        {linkedObservations != null && linkedObservations.length > 0 ? (
                          <div className="ot-form-label-inline">
                            <FormInput
                              label="Name (required)"
                              instruction=""
                              readOnly={readOnly || observationTypeData.IsProtected === 'Y'}
                              name="name"
                              id="name"
                            />
                            <span className="ot-form-label-suffix">{isOriginal ? '(Original)' : '(Derived)'}</span>
                          </div>
                        ) : (
                          <FormInput
                            label="Name (required)"
                            instruction=""
                            readOnly={readOnly || observationTypeData.IsProtected === 'Y'}
                            name="name"
                            id="name"
                          />
                        )}
                        <span style={{ display: 'flex', justifyContent: 'flex-end', fontSize: '.8rem' }}>
                          {readOnly || observationTypeData.IsProtected === 'Y' ? '' : '(50 Characters max)'}
                        </span>
                      </Col>
                      <Col
                        sm={setColSpanOrder(6, 2)}
                        md={setColSpanOrder(6, 2)}
                        lg={setColSpanOrder(4, 2)}
                        className="mb-4"
                      >
                        <FormInput
                          label="Display name (required)"
                          instruction=""
                          readOnly={readOnly || observationTypeData.IsProtected === 'Y'}
                          name="displayname"
                          id="displayname"
                        />
                        <span style={{ display: 'flex', justifyContent: 'flex-end', fontSize: '.8rem' }}>
                          {readOnly || observationTypeData.IsProtected === 'Y' ? '' : '(50 Characters max)'}
                        </span>
                      </Col>
                      <Col
                        sm={setColSpanOrder(6, 3)}
                        md={setColSpanOrder(6, 3)}
                        lg={setColSpanOrder(4, 3)}
                        className="mb-4"
                      >
                        <FormSelect
                          readOnly={readOnly}
                          label="Display unit (required)"
                          id="unitofmeasureid"
                          name="unitofmeasureid"
                          textValue={observationTypeData.UnitOfMeasureId ? observationTypeData.Abbreviation : ''}
                          options={convertDropdownOptions(allUnitOfMeasures)}
                          postChange={(e) => onUnitPostChange(e, props)}
                          isMulti={false}
                        />
                      </Col>
                      <Col
                        sm={setColSpanOrder(6, 4)}
                        md={setColSpanOrder(4, 4)}
                        lg={setColSpanOrder(4, 4)}
                        className="mb-4"
                      >
                        <FormRadioGroup
                          readOnly={readOnly}
                          label="Data type (required)"
                          textValue={getDataTypeName()}
                          name="isactive"
                          id="isactive"
                        >
                          <Row>
                            <Col sm={6}>
                              <FormRadio id="numeric" label="Numeric" name="isactive" value="Numeric" />
                            </Col>
                            <Col sm={5}>
                              <FormRadio id="string" label="String" name="isactive" value="String" />
                            </Col>
                          </Row>
                        </FormRadioGroup>
                      </Col>
                      <Col
                        sm={setColSpanOrder(12, 5)}
                        md={setColSpanOrder(12, 5)}
                        lg={setColSpanOrder(8, 5)}
                        className="mb-4"
                      >
                        <FormTextArea
                          readOnly={readOnly}
                          label="Definition"
                          instruction="(1,000 Characters max)"
                          id="definition"
                          name="definition"
                          rows={3}
                        />
                      </Col>
                      <Col
                        sm={setColSpanOrder(6, 6)}
                        md={setColSpanOrder(6, 6)}
                        lg={setColSpanOrder(4, 6)}
                        className="mb-4"
                      >
                        <FormSelect
                          readOnly={readOnly}
                          label="Standard graph axis"
                          id="standardgraphaxisid"
                          name="standardgraphaxisid"
                          isClearable={true}
                          postChange={(e) => {
                            if (e === null) {
                              //delete graph type
                              setTimeout(() => props.setFieldValue('graphvisualizationtypeid', '', false));
                              delete props.values.graphvisualizationtypeid;
                              //delete transform type
                              setTimeout(() => props.setFieldValue('visualvaluetransformtypeid', '', false));
                              delete props.values.visualvaluetransformtypeid;
                              // delete graph color
                              setTimeout(() => props.setFieldValue('graphcolor', null, false));
                              delete props.values.graphcolor;
                            }
                          }}
                          textValue={props.values.standardgraphaxisid?.value ?? ''}
                          options={standardGraphAxisOptions}
                          isMulti={false}
                        />
                      </Col>
                      <Col
                        sm={setColSpanOrder(6, 7)}
                        md={setColSpanOrder(6, 7)}
                        lg={setColSpanOrder(4, 7)}
                        className="mb-4"
                      >
                        <FormInput
                          label="Display format"
                          instruction=""
                          onChange={(e) => onChangeDataFormat(e, props)}
                          readOnly={readOnly || observationTypeData.IsProtected === 'Y'}
                          name="displayformat"
                          id="displayformat"
                          maxLength={3}
                        />
                        <span style={{ display: 'flex', justifyContent: 'flex-end', fontSize: '.8rem' }}>
                          {formatDisplayExample}
                        </span>
                      </Col>
                      <Col
                        sm={setColSpanOrder(6, 8)}
                        md={setColSpanOrder(6, 8)}
                        lg={setColSpanOrder(2, 8)}
                        className="mb-4"
                      >
                        <FormInput
                          label="Lower limit"
                          instruction=""
                          readOnly={readOnly || observationTypeData.IsProtected === 'Y'}
                          name="lowerlimit"
                          id="lowerlimit"
                          disabled={isDisableLimit(props)}
                        />
                      </Col>
                      <Col
                        sm={setColSpanOrder(6, 9)}
                        md={setColSpanOrder(6, 9)}
                        lg={setColSpanOrder(2, 9)}
                        className="mb-4"
                      >
                        <FormInput
                          label="Upper limit"
                          instruction=""
                          readOnly={readOnly || observationTypeData.IsProtected === 'Y'}
                          name="upperlimit"
                          id="upperlimit"
                          disabled={isDisableLimit(props)}
                        />
                      </Col>
                      <Col
                        sm={setColSpanOrder(6, 10)}
                        md={setColSpanOrder(6, 10)}
                        lg={setColSpanOrder(4, 10)}
                        className="mb-4"
                      >
                        <FormSelect
                          readOnly={readOnly}
                          label="Default graph type"
                          id="graphvisualizationtypeid"
                          name="graphvisualizationtypeid"
                          isClearable={true}
                          isDisabled={props.values.standardgraphaxisid?.value == null}
                          textValue={props.values.graphvisualizationtypeid?.label ?? ''}
                          postChange={(e) => {
                            if (e === null || e.label !== 'Bar') {
                              setTimeout(() => props.setFieldValue('visualvaluetransformtypeid', '', false));
                              delete props.values.visualvaluetransformtypeid;
                            }
                          }}
                          options={convertDropdownOptions(allAllGraphVisualizationTypes)}
                          isMulti={false}
                        />
                      </Col>
                      <Col
                        sm={setColSpanOrder(6, 11)}
                        md={setColSpanOrder(6, 11)}
                        lg={setColSpanOrder(4, 11)}
                        className="mb-4"
                      >
                        <FormSelect
                          readOnly={readOnly}
                          label="Transform type"
                          id="visualValueTransformTypeId"
                          name="visualvaluetransformtypeid"
                          isClearable={true}
                          isDisabled={props.values.graphvisualizationtypeid?.label !== 'Bar'}
                          textValue={observationTypeData.VisualValueTransformTypeName ?? ''}
                          options={convertDropdownOptions(allTransformTypes)}
                          isMulti={false}
                        />
                      </Col>
                      <Col
                        sm={setColSpanOrder(6, 12)}
                        md={setColSpanOrder(6, 12)}
                        lg={setColSpanOrder(4, 12)}
                        className="mb-4"
                      >
                        <FormColorInput
                          label="Graph colour"
                          name="graphcolor"
                          id="graphcolor"
                          readOnly={readOnly}
                          disabled={props.values.standardgraphaxisid?.value == null}
                        />
                      </Col>
                    </Row>
                    {linkedObservations != null && linkedObservations.length > 0 && (
                      <>
                        <FormListWithAction
                          label={isOriginal ? 'Derived Observations' : 'Original Observation'}
                          colLabel=""
                          items={linkedObservations}
                          readOnly={false}
                          hasActionLink={false}
                          columns={columns}
                        />
                      </>
                    )}
                    <Row>
                      <Col
                        sm={setColSpanOrder(6, 13)}
                        md={setColSpanOrder(6, 13)}
                        lg={setColSpanOrder(4, 13)}
                        className="mb-4"
                      >
                        {mode === FORM_MODE.CREATE ? (
                          <FormSelect
                            readOnly={readOnly}
                            label="Category (required)"
                            id="observationcategoryid"
                            name="observationcategoryid"
                            options={categoryDropdownOptions}
                            isMulti={false}
                            isClearable={false}
                            postChange={(e) => {
                              categoryOnChange(e);
                            }}
                          />
                        ) : (
                          <div className="my-1">
                            <label className="flex-grow-1">
                              <b>{'Category'}</b>
                            </label>
                            <div className="centered">
                              <span>{observationTypeData.ObservationCategoryDisplayName ?? 'Others'}</span>
                            </div>
                          </div>
                        )}
                      </Col>
                    </Row>
                    <CodeTableAdmin
                      observationTypeId={observationTypeData.Id}
                      mode={mode}
                      initialDto={initialDto}
                      setInitialDto={setInitialDto}
                      isAvalancheObs={isAvalancheObs}
                      saveCodeTable={saveCodeTable}
                      setSaveCodeTable={setSaveCodeTable}
                      props={props}
                      errors={codeTableErrors}
                      setErrors={setCodeTableErrors}
                    />
                  </Card.Body>
                  <Card.Footer>
                    {!showDeleteConfirm ? (
                      <Row className="my-2">
                        <AwpFormActionButtons
                          title=" "
                          entityName={entityName}
                          buttonLabel={buttonLabel}
                          mode={mode}
                          setMode={setMode}
                          onClickDelete={onClickDelete}
                          permissions={[Permissions.WriteObservationType]}
                          readOnly={readOnly}
                          isAlignLeft={true}
                          btnCancelName={'Close'}
                          props={props}
                          disableSave={
                            (entityErrors && Object.keys(entityErrors).length > 0) ||
                            (codeTableErrors && Object.keys(codeTableErrors).length > 0)
                          }
                          isPristine={isEqual(
                            crudService.cleanFormValues({ entity: props.values }),
                            crudService.cleanFormValues({ entity: initialDto }),
                          )}
                        />
                      </Row>
                    ) : (
                      <Row className="my-2">
                        <AwpYesNoDialog
                          action={deleteObservationType}
                          setShowYesNoConfirm={setShowDeleteConfirm}
                          props={props}
                        />
                      </Row>
                    )}
                  </Card.Footer>
                </Card>
              </Form>
            )}
          </Formik>
          <AwpValidationErrorModal setErrors={setEntityErrors} errors={entityErrors} />
        </>
      )}
    </>
  );
};

ObservationTypeEdit.propTypes = {
  setMode: PropTypes.func,
  mode: PropTypes.string,
  observationTypeId: PropTypes.number,
  data: PropTypes.array,
  setData: PropTypes.func,
  setLoading: PropTypes.func,
  loading: PropTypes.bool,
};

export default ObservationTypeEdit;
